import {
  CART_COOKIE,
  CART_EXPIRY,
  ADYEN_DONE_COOKIE_A,
  ADYEN_DONE_COOKIE_B,
  ADYEN_DONE_COOKIE_EXPIRY,
  ONE_YEAR_IN_SECONDS,
} from "@/config/cart";
import { getGoogleTokenForVariant } from "@/utils/googlePricing";
import type { CartItem } from "@server/gql/graphql";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import type { Variant } from "types";
import { z } from "zod";

export type IncomingItem = { item: Variant; quantity: number };

export function prepareIncomingItem(incomingItem: IncomingItem) {
  const { item, quantity } = incomingItem;

  const googlePv2Token = getGoogleTokenForVariant(item.id);
  const variantAttributes = Object.keys(
    JSON.parse(item.VariantDefiningAttributes ?? "{}"),
  );

  const variant = variantAttributes
    ?.map((attribute) => item[attribute])
    .join(", ");

  return {
    quantity,
    partNo: item.id,
    vatRate: item.vatRate,
    googlePv2Token,
    extras: {
      product_url: item.link,
      stock_status: item.articleStatus,
      variant,
    },
  };
}

export function setCartCookie({ id, token }: { id: string; token: string }) {
  void setCookie(CART_COOKIE, `${id}:${token}`, {
    maxAge: ONE_YEAR_IN_SECONDS,
    expires: CART_EXPIRY,
  });
}

export const AdyenDoneCookiePayload = z.object({
  sessionData: z.string(),
  sessionResult: z.string(),
  sessionId: z.string(),
  cartId: z.string(),
});

export const AdyenDoneCookiePayloadPartA = z.object({
  sessionData: z.string(),
  sessionId: z.string(),
});

export const AdyenDoneCookiePayloadPartB = z.object({
  sessionResult: z.string(),
  cartId: z.string(),
});

export type AdyenDoneCookiePayloadType = z.infer<typeof AdyenDoneCookiePayload>;

export function getAdyenDoneCookie(
  assumedCartId: string,
): AdyenDoneCookiePayloadType | null {
  const cookieValueA = getCookie(ADYEN_DONE_COOKIE_A);
  const cookieValueB = getCookie(ADYEN_DONE_COOKIE_B);
  if (typeof cookieValueA === "string" && typeof cookieValueB === "string") {
    try {
      const jsonedA = JSON.parse(cookieValueA);
      const jsonedB = JSON.parse(cookieValueB);
      const parsedA = AdyenDoneCookiePayloadPartA.safeParse(jsonedA);
      const parsedB = AdyenDoneCookiePayloadPartB.safeParse(jsonedB);
      if (parsedA.success && parsedB.success) {
        const joined = { ...parsedA.data, ...parsedB.data };
        if (joined.cartId == assumedCartId) {
          return joined as AdyenDoneCookiePayloadType;
        } else {
          removeAdyenDoneCookie();
        }
      }
    } catch {}
  }
  return null;
}

export function setAdyenDoneCookie(data: AdyenDoneCookiePayloadType) {
  const partA = JSON.stringify({
    sessionId: data.sessionId,
    sessionData: data.sessionData,
  });
  const partB = JSON.stringify({
    sessionResult: data.sessionResult,
    cartId: data.cartId,
  });
  void setCookie(ADYEN_DONE_COOKIE_A, partA, {
    maxAge: ONE_YEAR_IN_SECONDS,
    expires: ADYEN_DONE_COOKIE_EXPIRY,
  });
  void setCookie(ADYEN_DONE_COOKIE_B, partB, {
    maxAge: ONE_YEAR_IN_SECONDS,
    expires: ADYEN_DONE_COOKIE_EXPIRY,
  });
}

export function removeAdyenDoneCookie() {
  deleteCookie(ADYEN_DONE_COOKIE_A);
  deleteCookie(ADYEN_DONE_COOKIE_B);
}

export function cartItemSkeleton(item?: Partial<CartItem>) {
  return {
    partNo: String(Math.random() * 100) + "abc",
    quantity: 1,
    brand: "",
    id: 0,
    imageKey: "",
    isDiscounted: false,
    lineNo: 0,
    name: "",
    price: 0,
    priceListId: 0,
    priceOriginal: 0,
    totalGrossAmount: 0,
    totalVat: 0,
    vatPerItem: 0,
    ...item,
  };
}
